"use client"

import { useEffect, type ReactNode } from "react"

import { handleClientError } from "~/lib/client-error-handling"
import { normalizeUnhandledRejection } from "~/lib/logger-client"

/**
 * Client-side error logging provider
 *
 * Captures:
 * - Unhandled window errors (runtime errors)
 * - Unhandled promise rejections
 *
 * Sends errors to /api/log → server console → Vercel log drain → BetterStack
 *
 * NOTE: This component does NOT use React Error Boundaries because:
 * - Error boundaries at the root level can break the entire app UX
 * - Returning fallback UI removes navigation and makes the app unusable
 * - Window error handlers are sufficient for logging most errors
 * - React errors will still be logged via window.onerror in most cases
 * - Individual components should implement their own error boundaries if needed
 *
 * We don't intercept console.warn/error because:
 * - Client-side console output stays in the browser (not captured by Vercel)
 * - Would create circular logging if we used console in the logger
 * - Use clientLogger explicitly when you want client logs sent to server
 */
export function BetterStackLogProvider({
  children,
}: {
  children: ReactNode
}): ReactNode {
  useEffect(() => {
    function handleError(event: ErrorEvent): void {
      // Opaque cross-origin script errors surface as a null `error` with the
      // browser-sanitized message "Script error." and 0/0 coordinates — CORS
      // strips any detail, so they're unactionable and almost always third-party
      // scripts (analytics, browser extensions). Don't ship them to the drain.
      if (
        !event.error &&
        event.message === "Script error." &&
        event.lineno === 0 &&
        event.colno === 0
      ) {
        return
      }

      const runtimeError =
        event.error instanceof Error
          ? event.error
          : new Error(event.message)
      // GA4 mirroring happens inside `logClientError` (via handleClientError)
      // AFTER the drain confirms the ingest, so analytics dedupes exactly like
      // BetterStack.
      handleClientError(runtimeError, {
        type: "runtime_error",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      })
    }

    function handleRejection(event: PromiseRejectionEvent): void {
      const { error, diagnostics } = normalizeUnhandledRejection(event.reason)

      handleClientError(error, {
        type: "unhandled_rejection",
        ...diagnostics,
      })
    }

    window.addEventListener("error", handleError)
    window.addEventListener("unhandledrejection", handleRejection)

    return () => {
      window.removeEventListener("error", handleError)
      window.removeEventListener("unhandledrejection", handleRejection)
    }
  }, [])

  return children
}

